import {
  buildSpeakerColorMap,
  getSpeakerColor,
  isDisplayableDiarization,
  SpeakerColorEntry,
} from '@app/modules/diarization/speaker-utils';
import { TranscriptionType } from '@app/modules/logger/screens/stream/components/audit/transcriptions/transcriptions.types';
import { usePersonsByIds } from '@app/modules/persons/hooks/usePersonsByIds';
import { DateTimeStr, Station, TimeRange, toDateTimeStr, toTimestamp } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '../../../api';

/**
 * Fetches hour-bucketed transcriptions with diarization for a station range,
 * derives speaker colors, and batch-warms Person API cache for any person_ids.
 */
export function useDiarization(
  station: Station,
  range: TimeRange<DateTimeStr>,
  diarizationEnabled = true,
) {
  const { data, refetch, isFetched } = useQuery({
    queryKey: [['transcriptions-markers', station.slug, range.from, range.to]],
    queryFn: () =>
      apiClient.segments
        .fetchTranscription(station.slug, range.from, range.to, diarizationEnabled)
        .then((res) => res),
    enabled: diarizationEnabled,
  });

  const flatEntries = useMemo(() => {
    if (!isFetched || !diarizationEnabled || !data) {
      return [];
    }
    return data
      .flat()
      .flatMap((transcription: TranscriptionType) => transcription?.diarization ?? []);
  }, [isFetched, data, diarizationEnabled]);

  const personIds = useMemo(() => {
    return flatEntries.map((d) => d.person_id).filter((id): id is string => !!id);
  }, [flatEntries]);

  // Batch-fetch + warm individual person query cache for the whole stream range.
  usePersonsByIds(personIds);

  const speakers: SpeakerColorEntry[] = useMemo(
    () => buildSpeakerColorMap(flatEntries),
    [flatEntries],
  );

  const formatedData = useMemo(() => {
    if (!isFetched || !diarizationEnabled || !data) {
      return [];
    }

    const mappedData = data.flat().map((transcription: TranscriptionType) => {
      if (!transcription?.diarization?.length) {
        return null;
      }
      return transcription.diarization.map((d) => {
        if (!isDisplayableDiarization(d)) {
          return null;
        }
        return {
          time: toDateTimeStr(toTimestamp(transcription.time) + d.segment.start),
          distance: d.distance,
          label: d.label,
          segment: d.segment,
          voice_label: d.voice_label,
          person_id: d.person_id,
          color: getSpeakerColor(speakers, d),
        };
      });
    });

    return mappedData
      .flat()
      .filter((item): item is Exclude<typeof item, null> => item !== null);
  }, [isFetched, data, speakers, diarizationEnabled]);

  return {
    data: formatedData,
    refetch,
    speakers,
    rawData: data,
  };
}
